import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import { notificationFilterById } from "../../../../../actions/Utilities/NotificationFilter";
import AddButton from "../../../../../components/AddButton";
import ConfirmBoxDelete from "../../../../../components/ConfirmBoxDelete";
import EditButton from "../../../../../components/EditButton";
import MaterialTableTS from "../../../../../components/tables/MaterialTableTS";
import { toast } from "../../../../../components/toastNotification";
import { permissionsSelector } from "../../../../Login/selectors/permissionSelector";
import { PERMISSIONS } from "../../../../PermissionsConstants";

import {
  addStateCameraType1,
  selectedRowsStateCameraType1,
  editStateCameraType1,
  cameraType1FilterText,
  rowEditCameraType1,
  cameraType1Fetching,
  cameraType1ReFetching,
} from "./../atoms/atoms";
import CameraType1Form from "../form/CameraType1Form";
import useGetCameraType1 from "./../hooks/useGetCameraType1";
import useDeleteCameraType1 from "../hooks/useDeleteCameraType1";
import { ICameraType1, ICameraType1TableData } from "../types";
import Timer from "components/Timer";
import TableFilters from "components/TableFilters";

export const CameraType1Table: React.FC = () => {
  const { data: directData, refetch: reFetch } = useGetCameraType1();

  const BulkDelete = useDeleteCameraType1();

  //Atoms Call
  const [actionAddState, setActionAddState] =
    useRecoilState(addStateCameraType1);
  const [selectedRows, setSelectedRows] = useRecoilState(
    selectedRowsStateCameraType1
  );
  const [actionEditState, setActionEditState] =
    useRecoilState(editStateCameraType1);
  const [filterTextState, setFilterTextState] = useRecoilState(
    cameraType1FilterText
  );
  const setEditRowData = useSetRecoilState(rowEditCameraType1);
  const userPermissions = useRecoilValue(permissionsSelector);
  const [isFetching, setIsFetching] = useRecoilState(cameraType1Fetching);
  const isReFetching = useRecoilValue(cameraType1ReFetching);

  //Local States
  const [tableData, setTableData] = useState<ICameraType1[]>([]);
  const [rowsCount, setRowsCount] = useState(0);
  const [openModalDelete, setOpenModalDelete] = useState(false);
  const [tableFilteredData, setTableFilteredData] = useState<ICameraType1[]>(
    []
  );

  // Permission States
  const addPermission =
    PERMISSIONS.permission_utilities_securityconfiguration_cameratype1_add;
  const deletePermission =
    PERMISSIONS.permission_utilities_securityconfiguration_cameratype1_delete;
  const editPermission =
    PERMISSIONS.permission_utilities_securityconfiguration_cameratype1_edit;

  const Loader = async () => {
    setTableData(directData?.data.map((data) => ({ ...data })));
    await Timer();
    setIsFetching(false);
  };
  // Get Data
  useEffect(() => {
    reFetch();
  }, [isReFetching]);

  useEffect(() => {
    if (directData?.status === 400) {
      toast.fire("Server Error", directData?.data.Message, "error");
      setTableData([]);
      setIsFetching(false);
    } else {
      Loader();
    }
  }, [directData]);

  // For Recent Activity + Search
  let location = useLocation();
  let recordIdFromRecent;

  const [recent, setRecent] = useState("");
  if (location.state !== null) {
    recordIdFromRecent = location.state;
  }

  useEffect(() => {
    if (recordIdFromRecent) {
      notificationFilterById(
        recordIdFromRecent.recordId,
        recordIdFromRecent.moduleName,
        recordIdFromRecent.subModuleName
      ).then((result: ICameraType1[] | undefined) => {
        if (typeof result !== "object") {
          toast.fire("Failed", result, "error");
        } else {
          setTableFilteredData(result);
          setRecent(result[0].cameraType1Name);
        }
      });
    } else {
      localStorage.removeItem("NotifFilter");
      localStorage.removeItem("ModuleFilter");
    }
    setSelectedRows([]);
  }, [recordIdFromRecent]);

  //For Filter/Search
  const filtereditems =
    tableData &&
    tableData?.filter(
      (item) =>
        item.cameraType1Name &&
        item.cameraType1Name
          .toLowerCase()
          .includes(filterTextState?.trim().toLowerCase())
    );
  useEffect(() => {
    if (!recent) {
      setTableFilteredData(filtereditems);
    }
  }, [filterTextState]);

  const handleAdd = () => {
    setActionAddState(true);
  };

  const handleSelectedRowsChange = (rows: ICameraType1TableData[]) => {
    const forDelete = rows
      .filter((x) => x.isActive)
      .map((r) => {
        const { tableData, ...rows } = r;
        return rows;
      });
    setSelectedRows(forDelete);
    setRowsCount(forDelete.length);
  };

  const handleEdit = (rowData: ICameraType1TableData) => {
    delete rowData.tableData;
    setEditRowData({ ...rowData });
    setActionEditState(true);
  };

  const handleDelete = () => {
    setIsFetching(true);
    const ids = selectedRows && selectedRows.map((row) => row.cameraType1Id);
    BulkDelete(ids);
    setSelectedRows([]);
    setRecent("");
    setOpenModalDelete(false);
  };

  const deleteMessage = (
    <div className="text-gray-500 font-normal">{`Are you sure you want to deactivate ${rowsCount} ${
      rowsCount > 1 ? "Camera Type 1/s" : "Camera Type 1"
    }?`}</div>
  );

  const matColumns = [
    {
      title: "Camera Type 1 Name",
      field: "cameraType1Name",
      customSort: (a, b) =>
        a.cameraType1Name && b.cameraType1Name
          ? a.cameraType1Name.trim().localeCompare(b.cameraType1Name.trim())
          : a.cameraType1Name
          ? 0
          : -1,
    },
    {
      title: "Created By",
      field: "createdBy",
      customSort: (a, b) =>
        a.createdBy && b.createdBy
          ? a.createdBy.trim().localeCompare(b.createdBy.trim())
          : a.createdBy
          ? 0
          : -1,
    },
    {
      title: "Created Date",
      field: "createdDate",
      type: "date" as "date",
    },
    {
      title: "Modified By",
      field: "modifiedBy",
      customSort: (a, b) =>
        a.modifiedBy && b.modifiedBy
          ? a.modifiedBy.trim().localeCompare(b.modifiedBy.trim())
          : a.modifiedBy
          ? 0
          : -1,
    },
    {
      title: "Modified Date",
      field: "modifiedDate",
      type: "date" as "date",
    },
    {
      title: "Status",
      field: "isActive",
      render: (rowData) => {
        return rowData?.isActive ? "Active" : "Inactive";
      },
    },
    {
      title: "Action/s",
      align: "center" as "center",
      hidden: !userPermissions.includes(editPermission),
      sorting: false,
      render: (rowData) => {
        return (
          <div className="flex items-center place-content-center">
            <EditButton
              className={""}
              disabled={
                actionEditState ||
                (selectedRows !== undefined && selectedRows.length > 0)
              }
              onClick={() => {
                handleEdit(rowData);
              }}
            />
          </div>
        );
      },
    },
  ];

  return (
    <div className="bg-white p-4">
      {actionAddState || actionEditState ? (
        <CameraType1Form setRecent={setRecent} />
      ) : (
        <>
          <div className="flex justify-between text-xs items-center w-full">
            <TableFilters
              defaultText={filterTextState}
              recentText={recent}
              setRecent={setRecent}
              textInputName={"CameraType1Name"}
              columnDetails={"Camera Type 1 Name"}
              filterText={setFilterTextState}
              disabledButton={
                actionEditState ||
                (selectedRows !== undefined && selectedRows.length > 0)
              }
              disabledTextBox={
                selectedRows !== undefined && selectedRows.length > 0
              }
            />

            <div className="">
              {userPermissions.includes(addPermission) ? (
                <AddButton
                  buttonText="New Camera Type 1"
                  onClick={() => handleAdd()}
                  disabled={
                    actionEditState ||
                    (selectedRows !== undefined && selectedRows.length > 0)
                  }
                />
              ) : (
                ""
              )}
            </div>
          </div>
          <div>
            {userPermissions.includes(deletePermission) ? (
              <div className="text-secondary italic text-xs3 my-2">
                <p className="font-bold italic inline-block mr-1">Note:</p>
                <span>Tick the box to deactivate some data.</span>
              </div>
            ) : (
              <div className="mb-4"></div>
            )}
          </div>
          <div className="">
            <MaterialTableTS
              data={
                isFetching
                  ? []
                  : filterTextState === "" && recent === ""
                  ? tableData
                  : filterTextState === ""
                  ? tableFilteredData
                  : filtereditems
              }
              showTitle={true}
              columns={matColumns}
              isLoading={isFetching}
              onSelectionChange={handleSelectedRowsChange}
              selectedRows={selectedRows}
              selection={
                !actionEditState &&
                !actionAddState &&
                userPermissions.includes(deletePermission)
              }
              setConfirmModalOpen={setOpenModalDelete}
            />
          </div>

          <ConfirmBoxDelete
            ModalTitle="Deactivate Camera Type 1"
            qConfirmation={deleteMessage}
            isOpen={openModalDelete}
            onClose={() => {
              setOpenModalDelete(false);
            }}
            onClickFunction={handleDelete}
            cancelButtonRef={undefined}
          />
        </>
      )}
    </div>
  );
};
export default CameraType1Table;
